import { useQuery } from '@tanstack/react-query'
import { getApiUrl } from '@/lib/subdomain'
import { FortniteIcon } from '@/components/PlatformIcons'

const apiUrl = getApiUrl()
const PURPLE = '#9d4dbb'

interface FortniteStats {
  stats: {
    overall: {
      wins: number
      kills: number
      kd: number
      matches: number
      winRate: number
      minutesPlayed: number
    }
    solo?: { wins: number; kills: number; kd: number; matches: number }
    duo?: { wins: number; kills: number; kd: number; matches: number }
    squad?: { wins: number; kills: number; kd: number; matches: number }
  }
  battlePass?: { level: number; progress: number }
  accountName?: string
}

export function FortniteShowcase() {
  const { data: fortniteStats } = useQuery({
    queryKey: ['public', 'fortnite'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/public/stats/fortnite`)
      if (!res.ok) return null
      return res.json() as Promise<FortniteStats>
    },
  })

  if (!fortniteStats) return null

  const overall = fortniteStats.stats?.overall
  const modes = [
    fortniteStats.stats?.solo && { name: 'Solo', ...fortniteStats.stats.solo },
    fortniteStats.stats?.duo && { name: 'Duos', ...fortniteStats.stats.duo },
    fortniteStats.stats?.squad && { name: 'Squads', ...fortniteStats.stats.squad },
  ].filter(Boolean) as Array<{ name: string; wins: number; kills: number; kd: number; matches: number }>

  return (
    <section className="py-12 px-4">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-display font-bold">Fortnite Stats</h2>
            {fortniteStats.accountName && (
              <span className="text-sm text-gray-500">
                {fortniteStats.accountName}
              </span>
            )}
          </div>
          {fortniteStats.battlePass && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ borderColor: `${PURPLE}40`, backgroundColor: `${PURPLE}15` }}>
              <span className="text-sm font-bold" style={{ color: PURPLE }}>BP Lv. {fortniteStats.battlePass.level}</span>
            </div>
          )}
        </div>

        {/* Overall Stats */}
        {overall && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard label="Wins" value={overall.wins.toLocaleString()} />
            <StatCard label="Eliminations" value={overall.kills.toLocaleString()} />
            <StatCard label="K/D Ratio" value={overall.kd?.toFixed(2) || '0'} />
            <StatCard label="Win Rate" value={`${overall.winRate?.toFixed(1) || 0}%`} />
          </div>
        )}

        {/* Mode Breakdown */}
        {modes.length > 0 && (
          <div className={`grid gap-3 ${modes.length === 3 ? 'md:grid-cols-3' : modes.length === 2 ? 'md:grid-cols-2' : ''}`}>
            {modes.map((mode) => (
              <div
                key={mode.name}
                className="rounded-xl bg-gray-800/50 border border-gray-800 hover:border-[#9d4dbb]/30 transition-colors p-4"
              >
                <div className="flex items-center gap-2 mb-3">
                  <FortniteIcon size={14} color={PURPLE} />
                  <span className="font-bold">{mode.name}</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <div className="text-lg font-bold" style={{ color: PURPLE }}>{mode.wins}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Wins</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">{mode.kills}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Kills</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">{mode.kd?.toFixed(2) || '0'}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">K/D</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">{mode.matches}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Matches</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-800 hover:border-[#9d4dbb]/30 transition-colors text-center">
      <div className="text-2xl font-bold font-display" style={{ color: PURPLE }}>{value}</div>
      <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  )
}
